import { failingFetcher } from '@lib/http/fetch';
import type {
  LookupProductMembershipResponse,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';

const EMPTY_MEMBERSHIP: ProductMembership = {
  personal: [],
  organization: [],
};

const LOOKUP_CHUNK_SIZE = 50;

type MembershipMap = Record<string, ProductMembership>;

// Local write versions shared by providers using the same SWR cache and org.
// A lookup may replace cached data unless a save happened after it started.
const membershipWrites = new WeakMap<
  object,
  Map<string, Map<string, symbol>>
>();

// Lists explicitly deleted this session. Unlike an eventually-consistent gap
// in the list response, a successful DELETE is authoritative: cached,
// in-flight, and subsequent optimistic memberships must not restore its id.
// Module-scoped rather than provider state: the provider is page-scoped and
// the delete flow navigates away immediately, so per-provider state would
// discard the tombstone right after creating it; module scope also applies to
// membership caches keyed by other org ids. Assumes list ids are never
// restored or reused — a restore/undo feature would need to clear its id here.
const deletedListIds = new Set<string>();

// Test-only: module state would otherwise leak between tests.
export const clearDeletedListIdsForTesting = () => deletedListIds.clear();

const removeDeletedLists = (
  membership: ProductMembership,
  deletedIds: ReadonlySet<string>
): ProductMembership =>
  deletedIds.size === 0
    ? membership
    : {
        personal: membership.personal.filter((id) => !deletedIds.has(id)),
        organization: membership.organization.filter(
          (id) => !deletedIds.has(id)
        ),
      };

const buildLookupUrl = (encodedIds: string, organizationId?: string) => {
  const base = `/api/lists/products/lookup?product-ids=${encodedIds}`;
  return organizationId ? `${base}&org-id=${organizationId}` : base;
};

type ChunkedMembership = {
  // Membership for ids whose chunk resolved. Ids in a failed chunk are absent.
  merged: MembershipMap;
  // Ids whose chunk rejected — left unresolved so they stay loading and retry.
  failedIds: string[];
};

async function fetchMembershipChunked(
  productIds: string[],
  organizationId?: string
): Promise<ChunkedMembership> {
  const chunks: string[][] = [];
  for (let i = 0; i < productIds.length; i += LOOKUP_CHUNK_SIZE) {
    chunks.push(productIds.slice(i, i + LOOKUP_CHUNK_SIZE));
  }

  // allSettled (not all): one chunk failing must not discard the ids that
  // other chunks fetched successfully.
  const results = await Promise.allSettled(
    chunks.map((chunk) => {
      const ids = chunk.map(encodeURIComponent).join(',');
      return failingFetcher(
        buildLookupUrl(ids, organizationId)
      ) as Promise<LookupProductMembershipResponse>;
    })
  );

  const merged: MembershipMap = {};
  const failedIds: string[] = [];
  results.forEach((result, i) => {
    const chunk = chunks[i];
    if (result.status === 'rejected') {
      // Leave these ids unresolved (loading) rather than defaulting them to
      // "not in any list" — we don't know their membership, so they retry.
      console.error('Error fetching product membership:', result.reason);
      failedIds.push(...chunk);
      return;
    }
    // Default this chunk's ids to "not in any list" so a known-but-empty
    // product resolves to `false` (rather than the loading `null`) once data
    // is in, then overlay the actual memberships the response carried.
    for (const id of chunk) merged[id] = EMPTY_MEMBERSHIP;
    for (const [id, membership] of Object.entries(result.value.products)) {
      merged[id] = membership;
    }
  });

  return { merged, failedIds };
}

// A dialog that never opens is worse than one that opens with an incomplete
// selection, so waiting on the publisher organization is bounded.
export const ORG_SETTLE_TIMEOUT_MS = 5000;

export type Deferred = {
  settled: boolean;
  promise: Promise<void>;
  resolve: () => void;
};

export const createDeferred = (): Deferred => {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((res) => {
    resolve = res;
  });
  return { settled: false, promise, resolve };
};

export type ProductMembershipApi = {
  /** `ignoredListId`: a list that does not count as holding the product. */
  isProductInList: (
    productId: string,
    ignoredListId?: string
  ) => boolean | null;
  getMembership: (productId: string) => ProductMembership;
  fetchProductMembership: (
    productId: string
  ) => Promise<ProductMembership | null>;
  fetchBatchProductMembership: (productIds: string[]) => Promise<void>;
  refreshProductMembership: (productId: string) => Promise<ProductMembership>;
  setMembership: (productId: string, membership: ProductMembership) => void;
  removeListFromMembershipCache: (listId: string) => Promise<void>;
};

export function useProductMembership(
  shouldFetch: boolean,
  isOrgSettled: boolean,
  organizationId?: string
): ProductMembershipApi {
  const { cache } = useSWRConfig();
  const writeVersions = useMemo(() => {
    let organizations = membershipWrites.get(cache);
    if (!organizations) {
      organizations = new Map();
      membershipWrites.set(cache, organizations);
    }
    const key = organizationId ?? '';
    let versions = organizations.get(key);
    if (!versions) {
      versions = new Map();
      organizations.set(key, versions);
    }
    return versions;
  }, [cache, organizationId]);
  const { data, mutate } = useSWR<MembershipMap>(
    shouldFetch ? ['product-membership', organizationId ?? ''] : null,
    null,
    {
      // Local cache written through `mutate`. SWR swaps the `null` fetcher for
      // the app's global one, and the revalidate* flags don't stop every
      // revalidation: a key change after mount (the organization arriving)
      // still fetched, requesting `product-membership,<orgId>` as a relative
      // URL. Pausing makes SWR skip every revalidation; `mutate` still writes.
      isPaused: () => true,
    }
  );

  // Ids already fetched or in-flight, so re-registration never re-requests them.
  const seenRef = useRef<Set<string>>(new Set());
  // Lets a caller that needs a membership *now* await the request a card mount
  // already started instead of firing a second one.
  const inflightRef = useRef<Map<string, Promise<MembershipMap>>>(new Map());
  // Ids registered before a lookup was possible (no session or no organization
  // yet). Deferred, never dropped: consumers dedup their own registrations (see
  // `useProductListFavorites`), so an id declined here is never asked for again.
  const pendingRef = useRef<Set<string>>(new Set());
  // Lets a caller wait for the publisher organization instead of being told
  // "unknown" before it lands.
  const orgSettledRef = useRef<Deferred | null>(null);
  const orgSettled = (orgSettledRef.current ??= createDeferred());

  // The active org owns the cache entry (it's part of the SWR key). When it
  // changes, SWR hands us a fresh `data`, so drop the per-org dedup tracking
  // synchronously here — before child effects re-register ids for the new org.
  const orgRef = useRef(organizationId);
  if (orgRef.current !== organizationId) {
    orgRef.current = organizationId;
    seenRef.current = new Set();
    inflightRef.current = new Map();
  }

  const registerIds = useCallback(
    async (ids: string[]): Promise<MembershipMap> => {
      // Registering before the organization is known would issue an org-less
      // lookup, then flip the SWR key and leave every product with a blank
      // membership until a second round completes.
      if (!shouldFetch || !isOrgSettled) {
        for (const id of ids) if (id) pendingRef.current.add(id);
        return {};
      }
      const toFetch = ids.filter((id) => id && !seenRef.current.has(id));
      if (toFetch.length === 0) return {};
      for (const id of toFetch) seenRef.current.add(id);
      const versionsAtStart = new Map(
        toFetch.map((id) => [id, writeVersions.get(id)])
      );

      const request = (async (): Promise<MembershipMap> => {
        try {
          const { merged, failedIds } = await fetchMembershipChunked(
            toFetch,
            organizationId
          );
          const effective = await mutate(
            (prev) => {
              const next = { ...(prev ?? {}) };
              for (const [id, membership] of Object.entries(merged)) {
                // Preserve local writes from any provider made during this
                // request, while allowing fresh lookups to update old data.
                if (writeVersions.get(id) === versionsAtStart.get(id)) {
                  next[id] = removeDeletedLists(membership, deletedListIds);
                }
              }
              return next;
            },
            { revalidate: false }
          );
          // Successful chunks stick; only the ids whose chunk failed are
          // released so they re-fetch the next time they're registered.
          for (const id of failedIds) seenRef.current.delete(id);
          return effective ?? {};
        } catch (error) {
          console.error('Error fetching product membership:', error);
          // Unexpected failure (not a per-chunk rejection) — allow a full retry.
          for (const id of toFetch) seenRef.current.delete(id);
          return {};
        }
      })();

      for (const id of toFetch) inflightRef.current.set(id, request);
      try {
        return await request;
      } finally {
        for (const id of toFetch) {
          if (inflightRef.current.get(id) === request) {
            inflightRef.current.delete(id);
          }
        }
      }
    },
    [shouldFetch, isOrgSettled, organizationId, mutate, writeVersions]
  );

  useEffect(() => {
    if (!shouldFetch || !isOrgSettled || pendingRef.current.size === 0) return;
    const deferred = Array.from(pendingRef.current);
    pendingRef.current.clear();
    registerIds(deferred);
  }, [shouldFetch, isOrgSettled, registerIds]);

  // The `registerIds` a render closes over stays gated shut, so anything
  // re-entering after awaiting the gate must use the current one.
  const registerIdsRef = useRef(registerIds);
  registerIdsRef.current = registerIds;

  // `shouldFetch` is part of the condition because while the session loads,
  // `isOrgSettled` says "nothing to wait for" only because nothing was asked.
  if (shouldFetch && isOrgSettled && !orgSettled.settled) {
    orgSettled.settled = true;
    orgSettled.resolve();
  }

  // Returns the membership rather than only priming the cache: the Save-to-list
  // dialog seeds its selection once, on open, so it cannot wait for a re-render.
  // `null` means unknown — the lookup failed or the organization never settled.
  // Both returns filter the deletion tombstones, like every other read path.
  const fetchProductMembership = useCallback(
    async (productId: string): Promise<ProductMembership | null> => {
      if (!productId) return null;

      const resolved = data?.[productId];
      if (resolved) return removeDeletedLists(resolved, deletedListIds);

      const gate = orgSettledRef.current;
      if (shouldFetch && !isOrgSettled && gate && !gate.settled) {
        await Promise.race([
          gate.promise,
          new Promise<void>((res) => setTimeout(res, ORG_SETTLE_TIMEOUT_MS)),
        ]);
      }

      const inflight = inflightRef.current.get(productId);
      const merged = inflight
        ? await inflight
        : await registerIdsRef.current([productId]);
      const membership = merged[productId];
      return membership ? removeDeletedLists(membership, deletedListIds) : null;
    },
    [data, shouldFetch, isOrgSettled]
  );

  const fetchBatchProductMembership = useCallback(
    async (productIds: string[]) => {
      await registerIds(productIds);
    },
    [registerIds]
  );

  // null = membership not yet known (loading); false/true = resolved.
  // Reads filter through the deletion tombstones too: the delete-time eviction
  // only rewrites the org key active at delete time, so entries cached under
  // other org keys are cleaned up here instead.
  const isProductInList = useCallback(
    (productId: string, ignoredListId?: string): boolean | null => {
      if (!shouldFetch) return null;
      const membership = data?.[productId];
      if (membership === undefined) return null;
      const live = removeDeletedLists(membership, deletedListIds);
      return (
        live.personal.some((id) => id !== ignoredListId) ||
        live.organization.length > 0
      );
    },
    [shouldFetch, data]
  );

  const getMembership = useCallback(
    (productId: string): ProductMembership => {
      const membership = data?.[productId];
      return membership
        ? removeDeletedLists(membership, deletedListIds)
        : EMPTY_MEMBERSHIP;
    },
    [data]
  );

  // Reflect a just-saved change in the cache so the bookmark icon updates and
  // the modal re-seeds with the correct selection on reopen. We do NOT
  // revalidate: the add/remove API calls already succeeded, so this value is
  // authoritative, and the lookup endpoint is eventually consistent — a
  // background re-fetch can return the pre-save membership and clobber it.
  const setMembership = useCallback(
    (productId: string, membership: ProductMembership) => {
      // Mark as saved so an in-flight lookup's merge can't overwrite it.
      writeVersions.set(productId, Symbol());
      seenRef.current.add(productId);
      mutate(
        (prev) => ({
          ...(prev ?? {}),
          [productId]: removeDeletedLists(membership, deletedListIds),
        }),
        { revalidate: false }
      );
    },
    [mutate, writeVersions]
  );

  // Bypasses the cached entry to confirm a write the cache cannot know about,
  // then stores the fresh value. Rejects when the lookup fails.
  const refreshProductMembership = useCallback(
    async (productId: string): Promise<ProductMembership> => {
      const response = (await failingFetcher(
        buildLookupUrl(encodeURIComponent(productId), organizationId)
      )) as LookupProductMembershipResponse;
      const membership = removeDeletedLists(
        response.products[productId] ?? EMPTY_MEMBERSHIP,
        deletedListIds
      );
      // Before the organization settles the lookup omits `org-id`, so its
      // empty organization memberships must not be cached.
      if (shouldFetch && isOrgSettled) setMembership(productId, membership);
      return membership;
    },
    [organizationId, shouldFetch, isOrgSettled, setMembership]
  );

  const removeListFromMembershipCache = useCallback(
    async (listId: string) => {
      deletedListIds.add(listId);
      await mutate(
        (prev) => {
          if (!prev) return prev;
          return Object.fromEntries(
            Object.entries(prev).map(([productId, membership]) => [
              productId,
              removeDeletedLists(membership, deletedListIds),
            ])
          );
        },
        { revalidate: false }
      );
    },
    [mutate]
  );

  return useMemo(
    () => ({
      isProductInList,
      getMembership,
      fetchProductMembership,
      fetchBatchProductMembership,
      refreshProductMembership,
      setMembership,
      removeListFromMembershipCache,
    }),
    [
      isProductInList,
      getMembership,
      fetchProductMembership,
      fetchBatchProductMembership,
      refreshProductMembership,
      setMembership,
      removeListFromMembershipCache,
    ]
  );
}
